import React, { Suspense, useMemo, useState } from 'react'

import { Trans } from '@lingui/react/macro'
import { useLazyLoadQuery, graphql } from 'react-relay'
import { useLocation, useNavigate } from 'react-router-dom'

import { View, XStack, styled } from 'tamagui'

import { Text } from '../Text'
import { Link } from '../Link'
import { Spinner } from '../Spinner'
import { Group } from '../Group'
import { Icon } from '../Icon'

import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'
import { useThemeContext } from '../ThemeContext'

import { useAuthContext } from '@data/useAuthContext'
import { useLocale } from '@data/i18n/I18nProvider'
import { SUPPORTED_LOCALES } from '@data/i18n/locales'
import { LocaleToggle, LocaleOptions } from './LocaleSelector'

import {
  DynamicIslandProfileExpandQuery,
  DynamicIslandProfileExpandQuery$data,
} from './__generated__/DynamicIslandProfileExpandQuery.graphql'

export const DynamicIslandProfileExpand = () => {
  return (
    <Suspense fallback={<Spinner size='$5' color='$primaryColor' />}>
      <DynamicIslandProfileExpandContent />
    </Suspense>
  )
}

const DynamicIslandProfileExpandContent = () => {
  const data = useLazyLoadQuery<DynamicIslandProfileExpandQuery>(
    graphql`
      query DynamicIslandProfileExpandQuery {
        viewer {
          slugId

          ...UserPrimaryPhotoNew_user
        }
      }
    `,
    {}
  )

  if (data.viewer) {
    return <DynamicIslandProfileExpandContentAuthenticated data={data} />
  }

  return <DynamicIslandProfileExpandContentUnauthenticated />
}

const DynamicIslandProfileExpandContentAuthenticated = ({
  data,
}: {
  data: DynamicIslandProfileExpandQuery$data
}) => {
  const location = useLocation()
  const navigate = useNavigate()

  const { setAuthTokens } = useAuthContext()
  const { themeName, toggleTheme } = useThemeContext()

  const slugId = data.viewer!.slugId

  const activeIndex = useMemo(() => {
    if (location.pathname === `/${slugId}`) return 0
    if (location.pathname.startsWith(`/${slugId}/settings`)) return 1
    return undefined
  }, [location.pathname, slugId])

  const activeRoute = useMemo(() => {
    if (location.pathname === `/${slugId}`) return 'profile' as const
    if (location.pathname.startsWith(`/${slugId}/settings`)) return 'settings' as const
    return undefined
  }, [location.pathname, slugId])

  const [itemHovered, setItemHovered] = useState<
    'profile' | 'settings' | 'theme' | 'locale' | 'signOut' | undefined
  >()
  const [localeExpanded, setLocaleExpanded] = useState(false)
  const { locale: currentLocale, setLocale } = useLocale()

  const isIconActive = (key: typeof itemHovered) =>
    activeRoute === key || itemHovered === key

  // Sign Out index shifts when locale options are expanded
  const signOutIndex = localeExpanded ? 4 + SUPPORTED_LOCALES.length : 4

  return (
    <Group
      activeIndex={activeIndex}
      gap='$2'
      highlightedElementProps={{
        borderRadius: '$6',
        backgroundColor: '$backgroundAccent',
      }}
      marginHorizontal={-4}
    >
      <Group.Item index={0}>
        <Link
          href={`/${slugId}`}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('profile')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <UserPrimaryPhoto user={data.viewer!} size='$6' thinShadow />

            <Text><Trans>Profile</Trans></Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={1}>
        <Link
          href={`/${slugId}/settings`}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('settings')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                isIconActive('settings') ? '$backgroundPress' : '$backgroundStrong'
              }
            >
              <StyledIcon name='settings-filled' />
            </StyledIconWrapper>

            <Text><Trans>Settings</Trans></Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={2}>
        <XStack
          gap='$3'
          alignItems='center'
          padding='$1'
          paddingRight='$5'
          cursor='pointer'
          onMouseEnter={() => setItemHovered('theme')}
          onMouseLeave={() => setItemHovered(undefined)}
          onPress={toggleTheme}
        >
          <StyledIconWrapper
            backgroundColor={
              itemHovered === 'theme' ? '$backgroundPress' : '$backgroundStrong'
            }
          >
            <StyledIcon name={themeName === 'dark' ? 'weather-sun' : 'weather-moon'} />
          </StyledIconWrapper>

          <Text minWidth={78}>{themeName === 'dark' ? <Trans>Light Mode</Trans> : <Trans>Dark Mode</Trans>}</Text>
        </XStack>
      </Group.Item>

      <Group.Item index={3}>
        <LocaleToggle
          currentLocale={currentLocale}
          isHovered={itemHovered === 'locale'}
          expanded={localeExpanded}
          onHoverStart={() => setItemHovered('locale')}
          onHoverEnd={() => setItemHovered(undefined)}
          onPress={() => setLocaleExpanded((prev) => !prev)}
        />
      </Group.Item>

      {localeExpanded && (
        <LocaleOptions
          currentLocale={currentLocale}
          startIndex={4}
          onSelect={(loc) => {
            setLocale(loc)
            setLocaleExpanded(false)
          }}
        />
      )}

      <Group.Item index={signOutIndex}>
        <Link
          href='#'
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('signOut')}
          onMouseLeave={() => setItemHovered(undefined)}
          onPress={async (event) => {
            event.preventDefault()

            await setAuthTokens(undefined, undefined)

            navigate('/')
          }}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered === 'signOut' ? '$backgroundPress' : '$backgroundStrong'
              }
            >
              <StyledIcon name='sign-out-filled' />
            </StyledIconWrapper>

            <Text><Trans>Sign Out</Trans></Text>
          </XStack>
        </Link>
      </Group.Item>
    </Group>
  )
}

const DynamicIslandProfileExpandContentUnauthenticated = () => {
  const location = useLocation()
  const { themeName, toggleTheme } = useThemeContext()
  const [itemHovered, setItemHovered] = useState<
    'signIn' | 'signUp' | 'theme' | 'locale' | undefined
  >()
  const [localeExpanded, setLocaleExpanded] = useState(false)
  const { locale: currentLocale, setLocale } = useLocale()

  return (
    <Group
      activeIndex={undefined}
      gap='$2'
      highlightedElementProps={{
        borderRadius: '$6',
        backgroundColor: '$backgroundAccent',
      }}
      marginHorizontal={-4}
    >
      <Group.Item index={0}>
        <Link
          href='/sign-in'
          options={{ state: { backgroundLocation: location } }}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('signIn')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered === 'signIn' ? '$backgroundPress' : '$backgroundStrong'
              }
            >
              <StyledIcon name='person' />
            </StyledIconWrapper>

            <Text><Trans>Sign In</Trans></Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={1}>
        <Link
          href='/sign-up'
          options={{ state: { backgroundLocation: location } }}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('signUp')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered === 'signUp' ? '$backgroundPress' : '$backgroundStrong'
              }
            >
              <StyledIcon name='add' />
            </StyledIconWrapper>

            <Text><Trans>Sign Up</Trans></Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={2}>
        <XStack
          gap='$3'
          alignItems='center'
          padding='$1'
          paddingRight='$5'
          cursor='pointer'
          onMouseEnter={() => setItemHovered('theme')}
          onMouseLeave={() => setItemHovered(undefined)}
          onPress={toggleTheme}
        >
          <StyledIconWrapper
            backgroundColor={
              itemHovered === 'theme' ? '$backgroundPress' : '$backgroundStrong'
            }
          >
            <StyledIcon name={themeName === 'dark' ? 'weather-sun' : 'weather-moon'} />
          </StyledIconWrapper>

          <Text minWidth={78}>{themeName === 'dark' ? <Trans>Light Mode</Trans> : <Trans>Dark Mode</Trans>}</Text>
        </XStack>
      </Group.Item>

      <Group.Item index={3}>
        <LocaleToggle
          currentLocale={currentLocale}
          isHovered={itemHovered === 'locale'}
          expanded={localeExpanded}
          onHoverStart={() => setItemHovered('locale')}
          onHoverEnd={() => setItemHovered(undefined)}
          onPress={() => setLocaleExpanded((prev) => !prev)}
        />
      </Group.Item>

      {localeExpanded && (
        <LocaleOptions
          currentLocale={currentLocale}
          startIndex={4}
          onSelect={(loc) => {
            setLocale(loc)
            setLocaleExpanded(false)
          }}
        />
      )}
    </Group>
  )
}

export const StyledIconWrapper = styled(View, {
  animation: 'fastSpring',
  alignItems: 'center',
  justifyContent: 'center',
  // @ts-expect-error - borderRadius '50%' is a web-only value
  borderRadius: '50%',
  backgroundColor: '$backgroundStrong',
  color: '$color',
  width: '$6',
  height: '$6',
})

export const StyledIcon = styled(Icon, {
  size: '$5',
  padding: 0,
})
